import { gsap } from 'gsap';

// Lab archive (after ingamana.com): rows of 4:5 image studies that grow as they rise. At the start only the mark and
// the first row show, the row already at full size and running off both edges; the rows below wait under the fold
// at half size, fully inside the margins. Rows stack from the bottom, so a row that grows pushes the rows above it
// up: they rise faster than the scroll and leave at the top at about 6x, each roughly double the row below.
// Row heights match the reference (its tiles are 5:3), so the portrait tiles are narrower and a row holds as many
// as fit the reference's half-size span (about 15 on desktop). Rows alternate between the two sets in the list
// (data-set="1" / "2"), each taking the next run of its set; runs wrap, so an image repeats across rows but never
// within one. Layout is computed here and applied with transforms only (Guardrail 2); sizes follow a damped
// copy of the scroll (Guardrail 5).

const DAMPING = 0.08; // per-60Hz-frame approach of the size clock to the live scroll
const ROWS = 12; // ponytail: rows reuse images to keep the reference's scroll length; ROWS = 8 shows each about once

export function initLabArchive(): void {
  const section = document.getElementById('labArchive');
  const stage = section?.querySelector<HTMLElement>('.lab-archive-stage');
  const mark = section?.querySelector<HTMLElement>('.lab-archive-mark');
  const items = [...(section?.querySelectorAll<HTMLElement>('.lab-archive-list li') ?? [])];
  if (!section || !stage || !mark || !items.length) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  initMarkLoop(mark, reduce);
  if (reduce) return;

  const sets = ['1', '2'].map((s) => items.filter((li) => li.dataset.set === s)).filter((s) => s.length);
  const rowsEl = document.createElement('div');
  stage.append(rowsEl);
  section.classList.add('is-live');

  let W = 0, H = 0, g = 20, u = 0, mh = 0, c = 0, pitch = 0, sMin = 1, e = 0.5, t = 1, mobile = false;
  let per = 0, vStart = 0, vPin = 0, vEnd = 0, markH = 0, markY = 0;
  let rows: HTMLElement[] = [];

  function build(): void {
    rowsEl.textContent = '';
    rows = [];
    for (let k = 0; k < ROWS; k++) {
      const set = sets[k % sets.length], run = Math.floor(k / sets.length);
      const row = document.createElement('div');
      row.className = 'la-row';
      row.setAttribute('aria-hidden', 'true');
      for (let m = 0; m < per; m++) {
        const j = m - (per - 1) / 2;
        const card = document.createElement('div');
        card.className = 'la-card';
        card.style.setProperty('--k', String(j));
        card.style.setProperty('--delay', `${Math.max(0, 0.5 - 0.06 * (Math.abs(j) + 2))}s`);
        const inner = document.createElement('div');
        inner.className = 'la-in';
        const media = set[(run * per + m) % set.length].querySelector('.la-media')!.cloneNode(true) as HTMLElement;
        media.querySelector('img')?.setAttribute('loading', 'eager'); // a lazy frame would pop in mid-rise
        inner.append(media);
        card.append(inner);
        row.append(card);
      }
      rowsEl.append(row);
      rows.push(row);
    }
  }

  function measure(): void {
    W = section!.clientWidth;
    H = window.innerHeight;
    mobile = W < 1024;
    const small = W < 600;
    // The reference's card width: four of twenty columns (ten on mobile) at progress 1; mobile may shrink below it.
    let ref: number;
    if (mobile) {
      g = 16;
      ref = 0.4 * (W - 176) + 48;
      sMin = (32 + 0.3 * (W - 176)) / ref;
    } else {
      g = 20;
      ref = 0.2 * (W - 420) + 60;
      sMin = 0.5; // rows under the fold wait at half size, as on the reference
    }
    mh = ref * (W < 770 ? 1 : 212 / 354); // the reference's row height
    u = mh / 1.25; // our tiles are 4:5
    e = mobile ? 0.6 : 0.5;
    // As many tiles (odd, centred) as fit the reference's starting row span at the smallest size, at least 5.
    const side = small ? 2 : mobile ? 3 : 4;
    const span = (2 * side + 1) * e * ref + 2 * side * g;
    const fit = Math.floor((span + g) / (Math.max(sMin, e) * u + g));
    const next = Math.max(5, Math.min(fit - (fit % 2 ? 0 : 1), ...sets.map((s) => s.length)));
    if (next !== per || !rows.length) { per = next; build(); }
    section!.style.setProperty('--u', `${u}px`);
    section!.style.setProperty('--mh', `${mh}px`);
    section!.style.setProperty('--g', `${g}px`);
    section!.style.setProperty('--W', `${W}px`);
    c = mh;
    pitch = mh + 22 + g; // the reference row: card, its 22px caption line, gap (sets the pace; no caption here)
    t = (H / Math.floor(pitch)) * (1 - e) * (mobile ? 1.25 : 1);
    // Phones, as on the reference: no growth, a plain grid of small tiles (a fifth of the width) scrolling by.
    if (small) { e = sMin = (0.2 * W) / u; t = 0; }
    markH = mark!.getBoundingClientRect().height;
    // Start: mark and first row fill one screen. The stage unpins once the last row is in at the bottom (vPin), so
    // the next section follows with no empty band; the sizes keep running to vEnd while the stage scrolls away, so
    // the last rows grow too, upward from the stage's bottom edge.
    vStart = -(H - g - c);
    vPin = (ROWS - 1) * pitch - (H - g - c);
    vEnd = (ROWS - 1) * pitch;
    section!.style.height = `${H + vPin - vStart}px`;
  }

  // Row k's static top is k * pitch on the virtual scroll; r is how far it has risen, 0…1.
  function size(r: number): number {
    const q = e + t * r;
    const p = mobile ? q : q * Math.min(Math.max(1, q), t);
    return Math.max(sMin, p);
  }

  let v = 0, vr = 0;
  function target(): number {
    const top = section!.getBoundingClientRect().top;
    return vStart + gsap.utils.clamp(0, vEnd - vStart, -top);
  }

  function render(): void {
    // Sizes from the damped clock, positions stacked upward from the last row's fixed bottom.
    let bottom = (ROWS - 1) * pitch + c - Math.min(v, vPin);
    for (let k = ROWS - 1; k >= 0; k--) {
      const r = gsap.utils.clamp(0, 1, (vr + H - k * pitch) / H);
      const s = size(r);
      const h = Math.max(c, mh * s);
      const top = bottom - h;
      const row = rows[k];
      // Rows wholly off screen keep their last pose; nothing to paint there.
      if (top < H && bottom > -h) {
        row.style.setProperty('--s', s.toFixed(4));
        row.style.transform = `translate3d(0, ${top.toFixed(2)}px, 0)`;
        row.style.visibility = '';
      } else {
        row.style.visibility = 'hidden';
      }
      if (k === 0) { markY = top - g - markH; mark!.style.transform = `translate3d(0, ${markY.toFixed(2)}px, 0)`; }
      bottom = top - g;
    }
  }

  measure();
  v = vr = target();
  render();

  // No black band under the hero: the stage overlaps the hero (it is transparent and lets clicks through), and the
  // start pose is moved on until the mark sits just below the hero copy. The hero then scrolls away under it.
  function tuck(): void {
    const hero = section!.previousElementSibling as HTMLElement | null;
    const lift = hero?.offsetHeight ?? 0;
    section!.style.marginTop = `${-lift}px`;
    if (lift) {
      const want = lift - 30;
      let lo = -(H - g - c), hi = vPin;
      for (let i = 0; i < 30; i++) {
        const mid = (lo + hi) / 2;
        v = vr = mid; render();
        if (markY > want) lo = mid; else hi = mid;
      }
      vStart = lo;
      section!.style.height = `${H + vPin - vStart}px`;
    }
    v = vr = target(); render();
  }
  tuck();

  gsap.ticker.add(() => {
    const live = target();
    const k = 1 - Math.pow(1 - DAMPING, Math.min(gsap.ticker.deltaRatio(60), 3));
    const settled = live === v && Math.abs(live - vr) < 0.05;
    v = live;
    vr = Math.abs(live - vr) < 0.05 ? live : vr + (live - vr) * k;
    if (!settled) render();
  });

  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => { measure(); tuck(); }, 150);
  });
  document.fonts?.ready.then(() => { measure(); tuck(); });

  new IntersectionObserver(([entry], io) => {
    if (!entry.isIntersecting) return;
    section.classList.add('is-in');
    io.disconnect();
  }, { threshold: 0.1 }).observe(section);
}

// The mark (after ingamana.com): its glyphs and the logo run right to left round a cylinder seen from the front.
// Glyph edges sit at angles on the loop; each is projected to x = W/2 + W/2 * sin(angle), so glyphs widen towards
// the middle and squash at the edges, edge to edge with no gaps. The word repeats round the loop as often as keeps
// the middle stretch near 2x. Transforms only, on the shared ticker, and only while the mark is on screen.
const MARK_SPEED = 0.07; // loop travel per second, in mark widths

function initMarkLoop(mark: HTMLElement, reduce: boolean): void {
  const word = mark.querySelector<HTMLElement>('.lam-word');
  const logo = mark.querySelector<SVGElement>('.lam-logo');
  if (!word || !logo) return;
  const unit = [...[...(word.textContent ?? '')].map((ch) => ch), ' ', logo, ' '];
  word.remove(); logo.remove();

  let glyphs: { el: HTMLElement; s: number; w: number }[] = [], L = 0, W = 0, o = 0, visible = false;

  function build(): void {
    mark.textContent = '';
    W = mark.clientWidth;
    const make = (part: string | SVGElement) => {
      const el = document.createElement('span');
      el.className = 'lam-g';
      el.setAttribute('aria-hidden', 'true');
      if (typeof part === 'string') el.textContent = part;
      else el.append(part.cloneNode(true));
      mark.append(el);
      return el;
    };
    const one = unit.map(make);
    // Fit the box to the ink: cap height (R) above the baseline, descender (g) below, a little air both ways.
    // The logo is exactly cap height and stands on the baseline, so text and logo read as one line.
    const cs = getComputedStyle(mark);
    const ctx = document.createElement('canvas').getContext('2d')!;
    ctx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    const cap = ctx.measureText('RPnx').actualBoundingBoxAscent;
    const desc = Math.max(ctx.measureText('g').actualBoundingBoxDescent, 0);
    const air = parseFloat(cs.fontSize) * 0.04;
    const probe = document.createElement('i'); // an empty inline-block sits on the baseline: its top is the baseline
    probe.style.cssText = 'display:inline-block;width:0;height:0';
    one[0].append(probe);
    const baseline = probe.offsetTop;
    probe.remove();
    mark.style.height = `${cap + desc + 2 * air}px`;
    const place = (el: HTMLElement) => {
      const svg = el.querySelector('svg');
      if (svg) { svg.style.height = `${cap}px`; el.style.top = `${air}px`; } else el.style.top = `${air + cap - baseline}px`;
      return el;
    };
    one.forEach(place);
    const unitL = one.reduce((sum, el) => sum + el.offsetWidth, 0);
    const reps = Math.max(1, Math.round((Math.PI * W) / (2 * unitL)));
    const els = [...one, ...Array.from({ length: (reps - 1) * unit.length }, (_, i) => place(make(unit[i % unit.length])))];
    L = 0;
    glyphs = els.map((el) => { const g = { el, s: L, w: el.offsetWidth }; L += g.w; return g; });
  }

  function render(): void {
    const R = W / 2, k = (2 * Math.PI) / L, edge = Math.PI / 2;
    for (const g of glyphs) {
      let a = (((g.s - o) % L) + L) % L;
      if (a > L / 2) a -= L;
      const t1 = a * k, t2 = t1 + g.w * k;
      if (t2 <= -edge || t1 >= edge) { g.el.style.visibility = 'hidden'; continue; }
      const x1 = R + R * Math.sin(Math.max(-edge, t1)), x2 = R + R * Math.sin(Math.min(edge, t2));
      g.el.style.visibility = '';
      g.el.style.transform = `translate3d(${x1.toFixed(2)}px, 0, 0) scaleX(${((x2 - x1) / g.w).toFixed(4)})`;
    }
  }

  const start = () => { build(); render(); };
  if (document.fonts) document.fonts.ready.then(start); else start();
  let resizeTimer = 0;
  window.addEventListener('resize', () => { clearTimeout(resizeTimer); resizeTimer = window.setTimeout(start, 150); });
  if (reduce) return;
  new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; }).observe(mark);
  gsap.ticker.add(() => {
    if (!visible || !L) return;
    o = (o + MARK_SPEED * W * (gsap.ticker.deltaRatio(60) / 60)) % L;
    render();
  });
}
